import { Injectable } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Actions, Effect, ofType, OnInitEffects } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable, of, from } from 'rxjs';
import {
	map,
	switchMap,
	catchError,
	withLatestFrom,
	concatMap
} from 'rxjs/operators';
import { NzNotificationService } from 'ng-zorro-antd/notification';

import * as Reducers from '../reducers';
import * as UserActions from './actions';
import { AuthService } from '@app/services/auth/jwt/auth.service';
import { LoginDto } from '@api/auth/interfaces';

@Injectable()
export class UserEffects implements OnInitEffects {
	private mainPage = '/dashboard';

	constructor(
		private actions: Actions,
		private authService: AuthService,
		private router: Router,
		private route: ActivatedRoute,
		private rxStore: Store<any>,
		private notification: NzNotificationService
	) {}

	@Effect()
	readonly login: Observable<any> = this.actions.pipe(
		ofType(UserActions.LOGIN),
		map((action: UserActions.Login) => action.payload),
		concatMap(action =>
			of(action).pipe(
				withLatestFrom(this.rxStore.pipe(select(Reducers.getSettings)))
			)
		),
		switchMap(([payload, settings]) => {
			// jwt login
			if (settings.authProvider === 'jwt') {
				const loginDto: LoginDto = {
					username: payload.email,
					password: payload.password
				}
				return this.authService.login$(loginDto).pipe(
					switchMap(response => {
						if (response?.token) {
							this.notification.success(
								'Logged In',
								'You have successfully logged in!'
							);
							return of(new UserActions.LoadCurrentAccount(), new UserActions.LoginSuccessful({}));
						}
						this.notification.warning('Auth Failed', response.token);
						return of(new UserActions.LoginUnsuccessful());
					}),
					catchError((error: unknown) => {
						console.log('LOGIN ERROR: ', error);
						this.notification.error('Auth Failed', '');
						return from([{ type: UserActions.LOGIN_UNSUCCESSFUL }]);
					})
				);
			}
		})
	);

	@Effect()
	readonly loadCurrentAccount: Observable<any> = this.actions.pipe(
		ofType(UserActions.LOAD_CURRENT_ACCOUNT),
		map((action: UserActions.LoadCurrentAccount) => true),
		concatMap(action =>
			of(action).pipe(
				withLatestFrom(this.rxStore.pipe(select(Reducers.getSettings)))
			)
		),
		switchMap(([action, settings]) => {
			// jwt load current account
			if (settings.authProvider === 'jwt') {
				return this.authService.currentAccount$().pipe(
					map(response => {
						if (response && (response.email || response.user)) {
							this.navigateByURLParameter().then(result => {
								result || this.router
									.navigate([`/${this.mainPage}`])
									.catch(console.error);
							});
							response.userHash = this.authService.userHash
							return new UserActions.LoadCurrentAccountSuccessful(response);
						}
						return new UserActions.LoadCurrentAccountUnsuccessful();
					}),
					catchError((error: unknown) => {
						console.log('ACCOUNT LOAD ERROR: ', error);
						return from([{ type: UserActions.LOGIN_UNSUCCESSFUL }]);
					})
				);
			}

			// do nothing for firebase, as user state subscribed inside firebase service
			return of(new UserActions.EmptyAction());
		})
	);

	@Effect()
	readonly logout: Observable<any> = this.actions.pipe(
		ofType(UserActions.LOGOUT),
		map((action: UserActions.Logout) => true),
		concatMap(action =>
			of(action).pipe(
				withLatestFrom(this.rxStore.pipe(select(Reducers.getSettings)))
			)
		),
		switchMap(([, settings]) => {
			// jwt logout
			if (settings.authProvider === 'jwt') {
				return this.authService.logout$().pipe(
					map(() => {
						return from([{ type: UserActions.LOGOUT_SUCCESSFUL }]);
					}),
					catchError((error: unknown) => {
						console.log('ACCOUNT LOGOUT ERROR: ', error);
						return from([{ type: UserActions.LOGOUT_UNSUCCESSFUL }]);
					})
				).pipe(map((obj: {type: string}) => {
					switch(obj.type) {
						case UserActions.LOGOUT_SUCCESSFUL:
							this.notification.success(
								'Logged Out',
								'You have successfully logged out!'
							)
							break;
						case UserActions.LOGOUT_UNSUCCESSFUL:
							this.notification.warning(
								'Logged Out',
								'You have logged out!'
							)
							break;
					}

					this.router
						.navigate(['/auth/login'])
						.catch(console.error)

					return new UserActions.FlushUser()
				}))
			}
		})
	);

	@Effect()
	readonly lock: Observable<any> = this.actions.pipe(
		ofType(UserActions.LOCK_USER),
		map((action: UserActions.LockUser) => true),
		concatMap(action =>
			of(action).pipe(
				withLatestFrom(this.rxStore.pipe(select(Reducers.getSettings)))
			)
		),
		switchMap(([, settings]) => {
			// jwt logout
			if (settings.authProvider === 'jwt') {
				return this.authService.logout$().pipe(
					map(() => {
						let email;
						this.rxStore.pipe(select(Reducers.getUser)).subscribe(state => {
							email = state.email;
						});
						this.router
							.navigate(['/auth/lockscreen'])
							.catch(console.error);

						return new UserActions.LockedUser(email);
					})
				);
			}
		})
	);

	@Effect()
	readonly change_project: Observable<any> = this.actions.pipe(
		ofType(UserActions.CHANGE_PROJECT),
		map((action: UserActions.ChangeProject) => true),
		concatMap(action =>
			of(action).pipe(
				withLatestFrom(this.rxStore.pipe(select(Reducers.getSettings)))
			)
		),
		switchMap(([, settings]) => {
			return of(new UserActions.EmptyAction());
		})
	)

	ngrxOnInitEffects(): Action {
		return { type: UserActions.LOAD_CURRENT_ACCOUNT };
	}

	async navigateByURLParameter(): Promise<boolean> {
		if (!this.route.snapshot.queryParams.returnUrl) {
			return false;
		}

		const urlTree = this.router.parseUrl(this.route.snapshot.queryParams.returnUrl);
		const queryParams = urlTree.queryParams;
		const fragments = urlTree.fragment;
		urlTree.queryParams = {};
		urlTree.fragment = undefined;
		const link = decodeURIComponent(urlTree.toString());
		return await this.router.navigate([link], { queryParams: queryParams, fragment: fragments }).catch(() => false);
	}
}
